<template>
  <div class="page-container">
    <div class="page-title-container d-flex align-items-center justify-content-between">
      <h1 class="page-title mb-0">Plantillas WhatsApp</h1>
      <b-button variant="none" class="button button-primary" :disabled="syncing" @click="sync">
        <i class="ri-refresh-line mr-1"></i>
        <span v-if="syncing">Sincronizando…</span>
        <span v-else>Sincronizar con Twilio</span>
      </b-button>
    </div>

    <div class="table-container">
      <div class="table-header">
        <div class="w-full d-flex flex-wrap align-items-center">
          <div class="d-flex align-items-center mr-3 mb-2 mb-md-0">
            <label class="filter-label mb-0 mr-2" for="f-tipo">Tipo</label>
            <select id="f-tipo" v-model="filters.templateType" class="toolbar-select" @change="loadData">
              <option :value="null">Todos</option>
              <option v-for="t in typeOptions" :key="t.key" :value="t.key">{{ t.label }}</option>
              <option value="sin_clasificar">Sin clasificar</option>
            </select>
          </div>
          <div class="d-flex align-items-center mr-3 mb-2 mb-md-0">
            <label class="filter-label mb-0 mr-2" for="f-estado">Estado</label>
            <select id="f-estado" v-model="filters.approvalStatus" class="toolbar-select" @change="loadData">
              <option :value="null">Todos</option>
              <option value="approved">Aprobado</option>
              <option value="pending">Pendiente</option>
              <option value="rejected">Rechazado</option>
            </select>
          </div>
          <div class="d-flex align-items-center mr-3 mb-2 mb-md-0">
            <label class="filter-label mb-0 mr-2" for="f-idioma">Idioma</label>
            <select id="f-idioma" v-model="filters.language" class="toolbar-select" @change="loadData">
              <option :value="null">Todos</option>
              <option value="es">Español</option>
              <option value="en">Inglés</option>
            </select>
          </div>
          <button type="button" class="button-tooltip text-dark font-weight-bold" v-b-tooltip.hover
            title="Recargar" @click="loadData">
            <i class="ri-loop-right-line"></i>
          </button>
        </div>
      </div>

      <div class="table-responsive mb-0">
        <table class="custom-table w-100">
          <thead>
            <tr>
              <th class="pl-4 text-left">Nombre</th>
              <th class="text-center">Idioma</th>
              <th class="text-center">Tipo</th>
              <th class="text-center">Estado</th>
              <th class="text-left">Ciudades asignadas</th>
              <th class="text-center">Última sync</th>
              <th class="text-center pr-md-4">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="t in list" :key="t.id" :class="{ 'row-inactive': !t.is_active }">
              <td class="pl-4 template-name">
                {{ t.name }}
                <div v-if="!t.is_active" class="row-messages">ya no está en Twilio</div>
              </td>
              <td class="text-center">
                <span class="badge badge-gray">{{ t.language.toUpperCase() }}</span>
              </td>
              <td class="text-center">
                <span v-if="t.template_type" class="badge badge-blue">{{ typeLabel(t.template_type) }}</span>
                <span v-else class="text-muted">Sin clasificar</span>
              </td>
              <td class="text-center">
                <span :class="`badge ${statusMeta(t.approval_status).color}`">
                  {{ statusMeta(t.approval_status).label }}
                </span>
              </td>
              <td>
                <span v-if="t.assigned_cities.length">{{ t.assigned_cities.join(', ') }}</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="text-center text-muted small">{{ sinceText(t.synced_at) }}</td>
              <td class="text-center pr-md-3 text-nowrap">
                <button type="button" class="link-button mr-2" @click="openView(t)">
                  <i class="ri-eye-line"></i> Ver
                </button>
                <button type="button" class="link-button" @click="openConfig(t)">
                  <i class="ri-settings-3-line"></i> Configurar
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="table-footer d-flex justify-content-end align-items-center">
        <div class="table-no-results" v-if="!list.length">
          <p class="m-0 px-3">
            No hay plantillas. Pulsa «Sincronizar con Twilio» para traerlas.
          </p>
        </div>
      </div>
    </div>

    <!-- ═══════════ Modal: ver plantilla ═══════════ -->
    <b-modal v-model="view.show" :title="view.template ? view.template.name : ''" size="lg" hide-footer>
      <div v-if="view.template" class="row">
        <div class="col-md-5">
          <div class="info-item">
            <span class="info-label">Twilio SID</span>
            <span class="info-value mono">{{ view.template.twilio_sid }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Idioma</span>
            <span class="info-value">{{ view.template.language.toUpperCase() }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Estado</span>
            <span :class="`badge ${statusMeta(view.template.approval_status).color}`">
              {{ statusMeta(view.template.approval_status).label }}
            </span>
          </div>
          <div class="info-item">
            <span class="info-label">Parámetros</span>
            <span class="info-value">{{ view.template.param_count }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Actualizada en Twilio</span>
            <span class="info-value">{{ formatDateTime(view.template.twilio_updated_at) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Ciudades asignadas</span>
            <span class="info-value">
              {{ view.template.assigned_cities.length ? view.template.assigned_cities.join(', ') : '—' }}
            </span>
          </div>
        </div>

        <div class="col-md-7">
          <span class="info-label">Preview del mensaje</span>
          <div class="wa-preview">
            <div class="wa-bubble">
              <!-- El cuerpo se parte en {{N}} y texto para poder resaltar los slots -->
              <template v-for="(part, i) in bodyParts(view.template.body_text)">
                <span v-if="part.slot" :key="i" class="wa-slot">{{ part.text }}</span>
                <span v-else :key="i">{{ part.text }}</span>
              </template>
            </div>
          </div>
          <div v-if="view.template.param_count" class="mt-3">
            <span class="info-label">Parámetros</span>
            <div class="slot-list">
              <span v-for="n in view.template.param_count" :key="n" class="slot-chip">{{ slotLabel(n) }}</span>
            </div>
          </div>
        </div>
      </div>
    </b-modal>

    <!-- ═══════════ Modal: configurar ═══════════ -->
    <b-modal v-model="config.show" size="xl" hide-footer
      :title="config.template ? `Configurar: ${config.template.name}` : 'Configurar'">
      <div v-if="config.template">
        <div class="d-flex align-items-center mb-3">
          <label class="filter-label mb-0 mr-2" for="cfg-tipo">Tipo</label>
          <select id="cfg-tipo" v-model="config.templateType" class="toolbar-select">
            <option :value="null">Sin clasificar</option>
            <option v-for="t in typeOptions" :key="t.key" :value="t.key">{{ t.label }}</option>
          </select>
        </div>

        <template v-if="config.templateType === 'confirmacion'">
          <div class="d-flex align-items-center mb-3">
            <label class="filter-label mb-0 mr-2" for="cfg-dir">Dirección</label>
            <select id="cfg-dir" v-model="config.direction" class="toolbar-select" @change="onDirectionChange">
              <option value="APTO-HOT">APTO-HOT (del aeropuerto al hotel)</option>
              <option value="HOT-APTO">HOT-APTO (del hotel al aeropuerto)</option>
            </select>
            <span class="lang-hint ml-3">
              Idioma: <strong>{{ config.template.language.toUpperCase() }}</strong>
              <span class="text-muted"> (sale de la plantilla)</span>
            </span>
          </div>

          <div v-if="config.template.approval_status !== 'approved'" class="alert-inline">
            <i class="ri-error-warning-line"></i>
            Esta plantilla no está aprobada en WhatsApp ({{ statusMeta(config.template.approval_status).label }}),
            así que todavía no se puede asignar a ninguna ciudad.
          </div>

          <div class="row">
            <!-- Un solo mapeo para todas las ciudades -->
            <div class="col-lg-7">
              <h5 class="mb-1">Variables de la plantilla</h5>
              <p class="text-muted small mb-2">
                El mismo mapeo vale para todas las ciudades. Lo que cambia entre
                ellas —ciudad, aeropuerto y punto de encuentro— lo resuelve el
                sistema al enviar.
              </p>
              <table class="custom-table w-100 slot-table">
                <thead>
                  <tr>
                    <th class="text-left pl-3">Slot</th>
                    <th class="text-left">Campo del viaje</th>
                    <th class="text-left">Ejemplo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="n in config.template.param_count" :key="n">
                    <td class="pl-3 mono">{{ slotLabel(n) }}</td>
                    <td>
                      <select v-model="config.slots[n]" class="toolbar-select w-100">
                        <option :value="null">— elegir —</option>
                        <option v-for="f in fields" :key="f.field" :value="f.field">{{ f.label }}</option>
                      </select>
                    </td>
                    <td class="text-muted small">{{ exampleFor(config.slots[n]) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Las ciudades que usan esta plantilla -->
            <div class="col-lg-5">
              <h5 class="mb-1">Ciudades</h5>
              <p class="text-muted small mb-2">
                Marca las que deben usar esta plantilla. Al desmarcar una, se
                quita su asignación.
              </p>
              <div class="city-list">
                <label v-for="c in cities" :key="c.id" class="city-item">
                  <input
                    type="checkbox"
                    :value="c.id"
                    v-model="config.cityIds"
                    :disabled="config.template.approval_status !== 'approved'"
                  />
                  <span>{{ c.name }}</span>
                </label>
                <p v-if="!cities.length" class="text-muted small m-0">No hay ciudades activas.</p>
              </div>
              <p class="text-muted small mt-2 mb-0">
                {{ config.cityIds.length }} seleccionada{{ config.cityIds.length === 1 ? '' : 's' }}
              </p>
            </div>
          </div>
        </template>

        <!-- Un solo botón: guarda el tipo y las asignaciones juntos. Cuando
             eran dos, se podía guardar la asignación sin el tipo, y al reabrir
             el modal el tipo salía vacío y escondía todo lo asignado. -->
        <div class="d-flex justify-content-end mt-4">
          <b-button variant="none" class="button button-light mr-2" @click="config.show = false">Cancelar</b-button>
          <b-button variant="none" class="button button-primary" :disabled="config.saving" @click="saveConfig">
            <span v-if="config.saving">Guardando…</span>
            <span v-else>Guardar</span>
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { Alert } from 'wize-admin'

import { WhatsAppTemplateService, GeographyService } from '@/core/services'

// Catálogo de plantillas de WhatsApp (DEF-ADM-025). Twilio es la fuente del
// catálogo; acá se clasifica cada plantilla y se decide qué ciudad usa cuál.
export default {
  name: 'TemplateListPage',
  data() {
    return {
      list: [],
      cities: [],
      fields: [],
      syncing: false,
      filters: { templateType: null, approvalStatus: null, language: null },
      view: { show: false, template: null },
      config: {
        show: false,
        template: null,
        templateType: null,
        direction: 'APTO-HOT',
        // Un solo mapeo compartido: slot (número) -> campo del viaje.
        slots: {},
        // Ciudades marcadas para esta dirección.
        cityIds: [],
        // Lo que devolvió el GET, por dirección, para no perder la otra
        // dirección al cambiar de pestaña sin guardar.
        porDireccion: {},
        // Última dirección mostrada, para guardar lo editado al cambiar.
        direccionPrevia: 'APTO-HOT',
        saving: false
      },
      typeOptions: [
        { key: 'confirmacion', label: 'Confirmación' },
        { key: 'datos_chofer', label: 'Datos chofer' },
        { key: 'postventa', label: 'Postventa' },
        { key: 'totem', label: 'TOTEM' },
        { key: 'otro', label: 'Otro' }
      ]
    }
  },
  methods: {
    // El literal {{N}} no se puede escribir en la plantilla: el compilador de
    // Vue corta la interpolación en el primer }} que encuentra.
    slotLabel(n) {
      return '{{' + n + '}}'
    },
    typeLabel(key) {
      const found = this.typeOptions.find((t) => t.key === key)
      return found ? found.label : key
    },
    statusMeta(status) {
      if (status === 'approved') return { label: 'Aprobado', color: 'badge-green' }
      if (status === 'rejected') return { label: 'Rechazado', color: 'badge-red' }
      if (status === 'pending') return { label: 'Pendiente', color: 'badge-yellow' }
      return { label: 'Sin estado', color: 'badge-gray' }
    },
    exampleFor(field) {
      const f = this.fields.find((x) => x.field === field)
      return f ? f.example : '—'
    },
    formatDateTime(iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      if (isNaN(d)) return iso
      const p = (n) => String(n).padStart(2, '0')
      return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`
    },
    // "hace 2h" para la columna de última sincronización.
    sinceText(iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      if (isNaN(d)) return '—'
      const mins = Math.floor((Date.now() - d.getTime()) / 60000)
      if (mins < 1) return 'recién'
      if (mins < 60) return `hace ${mins} min`
      const hours = Math.floor(mins / 60)
      if (hours < 24) return `hace ${hours} h`
      return `hace ${Math.floor(hours / 24)} d`
    },
    // Parte el cuerpo en trozos de texto y slots, para resaltar los {{N}}.
    bodyParts(body) {
      const parts = []
      const re = /\{\{\s*\d+\s*\}\}/g
      let last = 0
      let m
      while ((m = re.exec(body)) !== null) {
        if (m.index > last) parts.push({ text: body.slice(last, m.index), slot: false })
        parts.push({ text: m[0], slot: true })
        last = m.index + m[0].length
      }
      if (last < body.length) parts.push({ text: body.slice(last), slot: false })
      return parts
    },

    async loadData() {
      try {
        await this.$store.dispatch('app/loading', true)
        const params = {}
        if (this.filters.templateType) params.template_type = this.filters.templateType
        if (this.filters.approvalStatus) params.approval_status = this.filters.approvalStatus
        if (this.filters.language) params.language = this.filters.language
        const res = await WhatsAppTemplateService.list(params)
        this.list = res.payload || []
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'No se pudieron obtener las plantillas')
      } finally {
        await this.$store.dispatch('app/loading', false)
      }
    },
    async loadCities() {
      try {
        const res = await GeographyService.listCities()
        // Alfabético: la lista de marcas se recorre con la vista, no por el
        // orden en que estén los ids.
        this.cities = (res.payload || [])
          .filter((c) => c.is_active)
          .sort((a, b) => a.name.localeCompare(b.name))
      } catch (e) {
        this.cities = []
      }
    },
    async loadFields() {
      try {
        const res = await WhatsAppTemplateService.fields()
        this.fields = res.payload || []
      } catch (e) {
        this.fields = []
      }
    },

    async sync() {
      this.syncing = true
      try {
        await this.$store.dispatch('app/loading', true)
        const res = await WhatsAppTemplateService.sync()
        const r = res.payload
        Alert.success(`Sincronizado: ${r.created} nuevas, ${r.updated} actualizadas, ${r.deactivated} desactivadas`)
        if (r.errors && r.errors.length) {
          Alert.error(`${r.errors.length} plantilla(s) con problemas: ${r.errors[0]}`)
        }
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'No se pudo sincronizar con Twilio')
      } finally {
        this.syncing = false
        await this.$store.dispatch('app/loading', false)
      }
    },

    openView(t) {
      this.view = { show: true, template: t }
    },

    async openConfig(t) {
      this.config = {
        show: true,
        template: t,
        templateType: t.template_type || null,
        direction: 'APTO-HOT',
        slots: {},
        cityIds: [],
        porDireccion: {},
        direccionPrevia: 'APTO-HOT',
        saving: false
      }
      try {
        const res = await WhatsAppTemplateService.getConfiguration(t.id)
        const p = res.payload || {}
        // El backend ya deduce el tipo de las asignaciones cuando la plantilla
        // quedó sin clasificar, así que lo que venga es lo que hay que mostrar.
        this.config.templateType = p.template_type || null

        const porDireccion = {}
        ;(p.configurations || []).forEach((c) => {
          porDireccion[c.direction] = {
            slots: this.toSlots(c.param_mapping),
            cityIds: [...(c.city_ids || [])]
          }
        })
        this.config.porDireccion = porDireccion

        // Se abre en la dirección que ya tenga configuración; si no hay
        // ninguna, en APTO-HOT, que es la de las plantillas universales.
        const direcciones = Object.keys(porDireccion)
        this.config.direction = direcciones.length ? direcciones[0] : 'APTO-HOT'
        this.config.direccionPrevia = this.config.direction
        this.aplicarDireccion(this.config.direction)
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'No se pudo obtener la configuración')
      }
    },
    // param_mapping del backend -> slots indexados por número, que es como los
    // recorre la tabla.
    toSlots(paramMapping) {
      const slots = {}
      Object.keys(paramMapping || {}).forEach((k) => {
        slots[parseInt(k, 10)] = paramMapping[k].field
      })
      return slots
    },
    // Carga en el formulario lo guardado para esa dirección. Vue 2 no detecta
    // claves nuevas en un objeto, así que el objeto se reemplaza entero.
    aplicarDireccion(direction) {
      const guardado = this.config.porDireccion[direction]
      this.config.slots = guardado ? { ...guardado.slots } : {}
      this.config.cityIds = guardado ? [...guardado.cityIds] : []
    },
    // Al cambiar de dirección se recuerda lo editado en la anterior, para no
    // perderlo si el operador vuelve antes de guardar.
    onDirectionChange() {
      const previa = this.config.direccionPrevia || null
      if (previa && previa !== this.config.direction) {
        this.config.porDireccion[previa] = {
          slots: { ...this.config.slots },
          cityIds: [...this.config.cityIds]
        }
      }
      this.aplicarDireccion(this.config.direction)
      this.config.direccionPrevia = this.config.direction
    },

    // Guarda tipo, mapeo y ciudades en una sola llamada. El backend lo resuelve
    // en una transacción: upsert de las marcadas y borrado de las demás.
    async saveConfig() {
      const t = this.config.template
      const total = t.param_count
      const tipo = this.config.templateType

      if (tipo !== 'confirmacion') {
        // Sin asignaciones que guardar: solo la clasificación.
        await this.saveTypeOnly(tipo)
        return
      }

      for (let n = 1; n <= total; n++) {
        if (!this.config.slots[n]) {
          Alert.error(`Falta elegir el campo del parámetro {{${n}}}`)
          return
        }
      }

      const paramMapping = {}
      for (let n = 1; n <= total; n++) {
        const field = this.config.slots[n]
        const def = this.fields.find((f) => f.field === field)
        paramMapping[String(n)] = { field, type: def ? def.type : 'string' }
      }

      this.config.saving = true
      try {
        await this.$store.dispatch('app/loading', true)
        const res = await WhatsAppTemplateService.configure(t.id, {
          template_type: tipo,
          direction: this.config.direction,
          param_mapping: paramMapping,
          city_ids: this.config.cityIds
        })
        const r = res.payload
        const nombres = (r.cities || []).map((c) => c.name).join(', ') || 'ninguna ciudad'
        Alert.success(`Guardado para ${nombres}` + (r.mappings_deleted ? ` (${r.mappings_deleted} quitada${r.mappings_deleted === 1 ? '' : 's'})` : ''))
        this.config.show = false
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'No se pudo guardar la configuración')
      } finally {
        this.config.saving = false
        await this.$store.dispatch('app/loading', false)
      }
    },
    // Una plantilla que no es de confirmación todavía no tiene asignaciones,
    // así que lo único que hay que guardar es su tipo.
    async saveTypeOnly(tipo) {
      this.config.saving = true
      try {
        await this.$store.dispatch('app/loading', true)
        await WhatsAppTemplateService.classify(this.config.template.id, tipo)
        Alert.success('Configuración guardada')
        this.config.show = false
        await this.loadData()
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'No se pudo guardar la configuración')
      } finally {
        this.config.saving = false
        await this.$store.dispatch('app/loading', false)
      }
    }
  },
  async mounted() {
    await Promise.all([this.loadData(), this.loadCities(), this.loadFields()])
  }
}
</script>

<style lang="stylus" scoped>
.toolbar-select
  height 34px
  padding 0 10px
  font-size 0.8rem
  border 1px solid #d1d5db
  border-radius 6px
  background-color #fff
  color #374151

.filter-label
  font-size 0.8rem
  color #6b7280
  white-space nowrap

.template-name
  font-size 0.82rem
  word-break break-all

.row-inactive
  background-color #f9fafb
  color #9ca3af

.row-messages
  margin 2px 0 0
  font-size 0.72rem
  color #b45309

.link-button
  background none
  border none
  padding 0
  color var(--color-primary)
  font-size 0.8rem
  cursor pointer
  &:hover
    text-decoration underline
  &--danger
    color #b91c1c

.mono
  font-family monospace

// --- Modal ver ---
.info-item
  display flex
  flex-direction column
  gap 2px
  margin-bottom 12px

.info-label
  font-size 11px
  text-transform uppercase
  letter-spacing .04em
  color #6c757d

.info-value
  font-size 0.85rem
  word-break break-all

.wa-preview
  margin-top 6px
  padding 14px
  border-radius 8px
  background-color #e5ddd5

.wa-bubble
  padding 10px 12px
  border-radius 8px
  background-color #dcf8c6
  font-size 0.84rem
  line-height 1.5
  white-space pre-wrap
  word-break break-word

.wa-slot
  padding 0 3px
  border-radius 3px
  background-color #bfdbfe
  color #1e40af
  font-weight 600

.slot-list
  display flex
  flex-wrap wrap
  gap 6px
  margin-top 6px

.slot-chip
  padding 2px 8px
  border-radius 999px
  background-color #eff6ff
  color #1e40af
  font-family monospace
  font-size 0.75rem

// --- Modal configurar ---
.lang-hint
  font-size 0.8rem
  color #374151

.city-list
  display flex
  flex-direction column
  gap 2px
  max-height 380px
  overflow-y auto
  padding 10px 12px
  border 0.3px solid #e5e7eb
  border-radius 6px

.city-item
  display flex
  align-items center
  gap 8px
  margin-bottom 0
  padding 5px 4px
  border-radius 4px
  font-size 0.85rem
  cursor pointer
  &:hover
    background-color #f9fafb
  input:disabled + span
    color #9ca3af

.slot-table
  font-size 0.8rem
  th
    font-size 0.75rem

.alert-inline
  padding 10px 12px
  margin-bottom 12px
  border 1px solid #fdba74
  border-radius 6px
  background-color #fff7ed
  color #c2410c
  font-size 0.82rem
</style>
